'use client'

import { oneToOneQueueAbi } from '@/config/abis/OneToOneQueue'
import { hyperEVM } from '@/config/chain/constants'
import type { MarketGetActionConfig } from '@/config/market-config'
import { queryClient } from '@/config/query-client'
import { getTokenImageUrl } from '@/domain/euler'
import { useAccount } from '@/domain/hooks/useAccount'
import { getBalancesQueryKeyPrefix } from '@/domain/wallet/getBalancesQueryKeyPrefix'
import { triggerWalletConnect } from '@/features/analytics'
import { type CommonDialogProps, type DialogConfig } from '@/features/dialogs/common/types'
import { getLatestReceiptOwnerIndexes } from '@/features/markets-overview/logic/getLatestReceiptOwnerIndexes'
import { Button, LinkButton } from '@/ui/atoms/button/Button'
import { Dash } from '@/ui/atoms/dash/Dash'
import { Dialog, DialogContent, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'
import { usePrivy } from '@privy-io/react-auth'
import { ExternalLink, Info } from 'lucide-react'
import { useMemo, useState } from 'react'
import { type Address, type Hex, erc20Abi, formatUnits, parseSignature, parseUnits } from 'viem'
import {
  useChainId,
  usePublicClient,
  useReadContract,
  useReadContracts,
  useSignTypedData,
  useWalletClient,
} from 'wagmi'

export interface MarketGetActionDialogProps extends CommonDialogProps {
  action: MarketGetActionConfig
  assetSymbol: string
  marketName: string
}

type MintStatus = {
  state: 'idle' | 'signing' | 'submitting' | 'success' | 'error'
  message?: string
  txHash?: Hex
}

const USDG0_BRIDGE_URL = 'https://usdg0.paxoslabs.com/#bridge'
const MAX_PENDING_ORDERS = 8
const MAX_ORDER_SCAN = 64

const ORDER_STATUS_LABELS = [
  'Not found',
  'Pending',
  'Complete',
  'Pre-filled',
  'Refunded',
  'Failed transfer',
  'Failed refund',
] as const

const EIP2612_TYPES = {
  Permit: [
    { name: 'owner', type: 'address' },
    { name: 'spender', type: 'address' },
    { name: 'value', type: 'uint256' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint256' },
  ],
} as const

const permitAbi = [
  {
    inputs: [{ internalType: 'address', name: 'owner', type: 'address' }],
    name: 'nonces',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'version',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

const feeModuleAbi = [
  {
    inputs: [
      { internalType: 'uint256', name: 'amount', type: 'uint256' },
      { internalType: 'contract IERC20', name: 'offerAsset', type: 'address' },
      { internalType: 'contract IERC20', name: 'wantAsset', type: 'address' },
      { internalType: 'address', name: 'receiver', type: 'address' },
    ],
    name: 'calculateOfferFees',
    outputs: [{ internalType: 'uint256', name: 'feeAmount', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
] as const

interface PermitApproval {
  approvalMethod: 1
  approvalV: number
  approvalR: Hex
  approvalS: Hex
  deadline: bigint
}

interface PendingOrder {
  orderIndex: bigint
  status: number
  amountOffer: bigint
  amountWant: bigint
}

function getButtonLabel(status: MintStatus, canUsePermit: boolean, permitUnavailable: boolean, symbol: string): string {
  if (status.state === 'signing') return 'Signing...'
  if (status.state === 'submitting') return 'Submitting...'
  if (status.state === 'success') return 'Order submitted'
  if (permitUnavailable) return 'Permit unavailable'
  if (canUsePermit) return 'Sign & mint'
  return `Preparing ${symbol}...`
}

function formatReceiveAmount(
  parsedAmount: bigint | undefined,
  feeAmount: bigint | undefined,
  wantTokenDecimals: number,
): string | undefined {
  if (parsedAmount === undefined || parsedAmount === 0n) return '0'
  if (feeAmount === undefined) return undefined
  const netAmount = parsedAmount - feeAmount
  if (netAmount <= 0n) return '0'
  return formatUnits(netAmount, wantTokenDecimals)
}

function formatFeeAmount(feeAmount: bigint | undefined, decimals: number): string {
  if (feeAmount === undefined || feeAmount === 0n) return '0.0000'
  return Number(formatUnits(feeAmount, decimals)).toFixed(4)
}

function formatFeePercent(parsedAmount: bigint | undefined, feeAmount: bigint | undefined): string {
  if (parsedAmount === undefined || parsedAmount === 0n || feeAmount === undefined) return '-'
  const basisPoints = (feeAmount * 1_000_000n) / parsedAmount
  return `${(Number(basisPoints) / 10_000).toFixed(2)}%`
}

function formatTokenAmount(amount: bigint, decimals: number): string {
  return Number(formatUnits(amount, decimals)).toLocaleString(undefined, {
    maximumFractionDigits: decimals,
  })
}

function shortenQueueAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}

function shortenHash(hash: Hex): string {
  return `${hash.slice(0, 6)}...${hash.slice(-4)}`
}

function normalizeSignatureV(signature: ReturnType<typeof parseSignature>): number {
  const v = signature.v !== undefined ? Number(signature.v) : (signature.yParity ?? 0) + 27
  return v < 27 ? v + 27 : v
}

function PendingOrdersPanel({
  account,
  queueAddress,
  action,
  assetSymbol,
}: {
  account: Address | undefined
  queueAddress: Address
  action: MarketGetActionConfig
  assetSymbol: string
}) {
  const { data: latestOrder, isLoading: isLoadingLatestOrder } = useReadContract({
    address: queueAddress,
    abi: oneToOneQueueAbi,
    functionName: 'latestOrder',
    chainId: hyperEVM.id,
    query: {
      enabled: !!account,
    },
  })

  const recentOrderIndexes = useMemo(() => getLatestReceiptOwnerIndexes(latestOrder, MAX_ORDER_SCAN), [latestOrder])
  const ownerContracts = useMemo(
    () =>
      account
        ? recentOrderIndexes.map((orderIndex) => ({
            address: queueAddress,
            abi: oneToOneQueueAbi,
            functionName: 'ownerOf',
            args: [orderIndex],
            chainId: hyperEVM.id,
          }))
        : [],
    [account, queueAddress, recentOrderIndexes],
  )

  const { data: ownerResults, isLoading: isLoadingOwners } = useReadContracts({
    contracts: ownerContracts,
    query: {
      enabled: !!account && recentOrderIndexes.length > 0,
    },
  })

  const ownerResultsForOrders = ownerResults as
    | readonly { status: 'success' | 'failure'; result?: Address }[]
    | undefined
  const orderIndexes = useMemo(() => {
    return recentOrderIndexes.filter((_, index) => {
      const owner = ownerResultsForOrders?.[index]
      return owner?.status === 'success' && owner.result?.toLowerCase() === account?.toLowerCase()
    })
  }, [account, ownerResultsForOrders, recentOrderIndexes])

  const statusContracts = useMemo(
    () =>
      orderIndexes.map((orderIndex) => ({
        address: queueAddress,
        abi: oneToOneQueueAbi,
        functionName: 'getOrderStatus',
        args: [orderIndex],
        chainId: hyperEVM.id,
      })),
    [orderIndexes, queueAddress],
  )
  const queueContracts = useMemo(
    () =>
      orderIndexes.map((orderIndex) => ({
        address: queueAddress,
        abi: oneToOneQueueAbi,
        functionName: 'queue',
        args: [orderIndex],
        chainId: hyperEVM.id,
      })),
    [orderIndexes, queueAddress],
  )

  const { data: statusResults, isLoading: isLoadingStatuses } = useReadContracts({
    contracts: statusContracts,
    query: {
      enabled: orderIndexes.length > 0,
    },
  })
  const { data: queueResults, isLoading: isLoadingOrders } = useReadContracts({
    contracts: queueContracts,
    query: {
      enabled: orderIndexes.length > 0,
    },
  })
  const statusResultsForOrders = statusResults as
    | readonly { status: 'success' | 'failure'; result?: number }[]
    | undefined
  const queueResultsForOrders = queueResults as
    | readonly {
        status: 'success' | 'failure'
        result?: readonly [bigint, bigint, Address, Address, Address, number, boolean]
      }[]
    | undefined

  const orders = useMemo<PendingOrder[]>(() => {
    return orderIndexes.flatMap((orderIndex, index) => {
      const status = statusResultsForOrders?.[index]
      const order = queueResultsForOrders?.[index]
      if (status?.status !== 'success' || order?.status !== 'success') return []
      if (!order.result || status.result === undefined) return []

      return [
        {
          orderIndex,
          status: Number(status.result),
          amountOffer: order.result[0],
          amountWant: order.result[1],
        },
      ]
    })
  }, [orderIndexes, queueResultsForOrders, statusResultsForOrders])

  const pendingOrders = orders.filter((order) => order.status === 1).slice(0, MAX_PENDING_ORDERS)
  const isLoading = isLoadingLatestOrder || isLoadingOwners || isLoadingStatuses || isLoadingOrders

  return (
    <aside className="no-scrollbar flex max-h-[600px] min-h-[320px] flex-col overflow-y-auto rounded-xl border border-white/[0.08] bg-white/[0.025] p-5 lg:mt-[60px]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold text-foreground text-lg">Pending</div>
          <div className="text-muted-foreground text-xs">Queue receipts in your wallet</div>
        </div>
        <div className="rounded-full bg-[#c5f357]/10 px-3 py-1 font-semibold text-[#c5f357] text-xs">
          {pendingOrders.length}
        </div>
      </div>

      <div className="mt-5 flex flex-1 flex-col gap-3">
        {!account ? (
          <div className="flex flex-1 items-center justify-center text-center text-muted-foreground text-sm">
            Connect your wallet to view pending orders.
          </div>
        ) : isLoading ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground text-sm">Loading orders...</div>
        ) : pendingOrders.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-center text-muted-foreground text-sm">
            No pending orders.
          </div>
        ) : (
          pendingOrders.map((order) => (
            <div
              key={order.orderIndex.toString()}
              className="rounded-xl border border-white/[0.06] bg-black/20 p-4 text-sm"
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="font-medium text-foreground">Order #{order.orderIndex.toString()}</span>
                <span className="rounded-full bg-[#c5f357]/10 px-2.5 py-1 font-medium text-[#c5f357] text-xs">
                  {ORDER_STATUS_LABELS[order.status] ?? 'Unknown'}
                </span>
              </div>
              <div className="flex flex-col gap-2 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Paid</span>
                  <span className="font-medium text-foreground">
                    {formatTokenAmount(order.amountOffer, action.offerTokenDecimals)} {action.offerTokenSymbol}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">Receive</span>
                  <span className="font-medium text-foreground">
                    {formatTokenAmount(order.amountWant, action.wantTokenDecimals)}{' '}
                    {action.wantTokenSymbol || assetSymbol}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <a
        href={USDG0_BRIDGE_URL}
        target="_blank"
        rel="noreferrer"
        className="mt-auto flex items-center justify-center gap-1.5 pt-4 text-muted-foreground text-xs transition-colors hover:text-foreground"
      >
        View on Paxos
        <ExternalLink className="h-3 w-3" />
      </a>
    </aside>
  )
}

function MarketGetActionDialog({ open, setOpen, action, assetSymbol }: MarketGetActionDialogProps) {
  const wantTokenSymbol = action.wantTokenSymbol || assetSymbol
  const wantTokenImage = getTokenImageUrl(undefined, wantTokenSymbol)
  const offerTokenImage = getTokenImageUrl(undefined, action.offerTokenSymbol)
  const account = useAccount()
  const chainId = useChainId()
  const publicClient = usePublicClient()
  const { data: walletClient } = useWalletClient()
  const { signTypedDataAsync } = useSignTypedData()
  const { connectWallet } = usePrivy()
  const [amount, setAmount] = useState('')
  const [status, setStatus] = useState<MintStatus>({ state: 'idle' })

  const offerTokenAddress = action.offerTokenAddress as Address
  const queueAddress = action.queueAddress as Address
  const wantTokenAddress = action.wantTokenAddress as Address

  const parsedAmount = useMemo(() => {
    try {
      return amount ? parseUnits(amount, action.offerTokenDecimals) : 0n
    } catch {
      return undefined
    }
  }, [action.offerTokenDecimals, amount])

  const { data: balance, refetch: refetchBalance } = useReadContract({
    address: offerTokenAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: account ? [account] : undefined,
    chainId: hyperEVM.id,
    query: {
      enabled: !!account,
    },
  })

  const {
    data: permitTokenName,
    isLoading: isPermitTokenNameLoading,
    isError: isPermitTokenNameError,
  } = useReadContract({
    address: offerTokenAddress,
    abi: erc20Abi,
    functionName: 'name',
    chainId: hyperEVM.id,
    query: {
      enabled: !!account,
    },
  })

  const {
    data: permitNonce,
    isLoading: isPermitNonceLoading,
    isError: isPermitNonceError,
  } = useReadContract({
    address: offerTokenAddress,
    abi: permitAbi,
    functionName: 'nonces',
    args: account ? [account] : undefined,
    chainId: hyperEVM.id,
    query: {
      enabled: !!account,
      retry: false,
    },
  })

  const {
    data: permitTokenVersion,
    isLoading: isPermitTokenVersionLoading,
    isError: isPermitTokenVersionError,
  } = useReadContract({
    address: offerTokenAddress,
    abi: permitAbi,
    functionName: 'version',
    chainId: hyperEVM.id,
    query: {
      enabled: !!account,
      retry: false,
    },
  })

  const { data: feeModuleAddress } = useReadContract({
    address: queueAddress,
    abi: oneToOneQueueAbi,
    functionName: 'feeModule',
    chainId: hyperEVM.id,
  })

  const { data: minimumOrderSize } = useReadContract({
    address: queueAddress,
    abi: oneToOneQueueAbi,
    functionName: 'minimumOrderSizePerAsset',
    args: [offerTokenAddress],
    chainId: hyperEVM.id,
  })

  const amountIsValid = parsedAmount !== undefined && parsedAmount > 0n

  const { data: contractFeeAmount } = useReadContract({
    address: feeModuleAddress,
    abi: feeModuleAbi,
    functionName: 'calculateOfferFees',
    args: amountIsValid && account ? [parsedAmount, offerTokenAddress, wantTokenAddress, account] : undefined,
    chainId: hyperEVM.id,
    query: {
      enabled: !!feeModuleAddress && amountIsValid && !!account,
    },
  })

  const exceedsBalance = amountIsValid && balance !== undefined && parsedAmount > balance
  const isBelowMinimum = amountIsValid && minimumOrderSize !== undefined && parsedAmount < minimumOrderSize
  const canUsePermit =
    !!account && permitTokenName !== undefined && permitNonce !== undefined && permitTokenVersion !== undefined
  const isLoadingPermit =
    amountIsValid &&
    !!account &&
    !isPermitTokenNameError &&
    !isPermitNonceError &&
    !isPermitTokenVersionError &&
    (isPermitTokenNameLoading || isPermitNonceLoading || isPermitTokenVersionLoading)
  const permitUnavailable =
    amountIsValid && !!account && (isPermitTokenNameError || isPermitNonceError || isPermitTokenVersionError)
  const isBusy = status.state === 'signing' || status.state === 'submitting'
  const canSubmit =
    amountIsValid &&
    !exceedsBalance &&
    !isBelowMinimum &&
    !!account &&
    !!publicClient &&
    !!walletClient &&
    canUsePermit &&
    !isBusy &&
    !isLoadingPermit
  const balanceLabel = balance !== undefined ? formatUnits(balance, action.offerTokenDecimals) : undefined
  const receiveAmount = formatReceiveAmount(parsedAmount, contractFeeAmount, action.wantTokenDecimals)
  const feeAmount = formatFeeAmount(contractFeeAmount, action.offerTokenDecimals)
  const feePercent = formatFeePercent(parsedAmount, contractFeeAmount)
  const minimumOrderSizeLabel =
    minimumOrderSize !== undefined ? formatTokenAmount(minimumOrderSize, action.offerTokenDecimals) : undefined
  const transactionUrl = status.txHash ? `${hyperEVM.blockExplorers.default.url}/tx/${status.txHash}` : undefined

  const handleMax = () => {
    if (balance === undefined) return
    setAmount(formatUnits(balance, action.offerTokenDecimals))
  }

  const signPermitApproval = async (): Promise<PermitApproval> => {
    if (
      !account ||
      permitTokenName === undefined ||
      permitNonce === undefined ||
      permitTokenVersion === undefined ||
      parsedAmount === undefined
    ) {
      throw new Error('Permit is not available for this token.')
    }

    const deadline = BigInt(Math.floor(Date.now() / 1000) + 60 * 60 * 24)
    setStatus({ state: 'signing' })
    const signatureData = await signTypedDataAsync({
      types: EIP2612_TYPES,
      primaryType: 'Permit',
      domain: {
        name: permitTokenName,
        version: permitTokenVersion,
        chainId,
        verifyingContract: offerTokenAddress,
      },
      message: {
        owner: account,
        spender: queueAddress,
        value: parsedAmount,
        nonce: permitNonce,
        deadline,
      },
    })
    const signature = parseSignature(signatureData)

    return {
      approvalMethod: 1,
      approvalV: normalizeSignatureV(signature),
      approvalR: signature.r,
      approvalS: signature.s,
      deadline,
    }
  }

  const handleSubmit = async () => {
    if (!account) {
      void triggerWalletConnect(connectWallet, 'market_get_usdg0_mint')
      return
    }
    if (!canSubmit || parsedAmount === undefined) return

    try {
      if (chainId !== hyperEVM.id) {
        setStatus({
          state: 'error',
          message: `Switch your wallet to ${hyperEVM.name} to mint ${action.wantTokenSymbol || assetSymbol}.`,
        })
        return
      }

      const permitApproval = await signPermitApproval()

      setStatus({ state: 'submitting' })
      const signatureParams = {
        approvalMethod: permitApproval.approvalMethod,
        approvalV: permitApproval.approvalV,
        approvalR: permitApproval.approvalR,
        approvalS: permitApproval.approvalS,
        submitWithSignature: false,
        deadline: permitApproval.deadline,
        eip2612Signature: '0x' as Hex,
        nonce: 0n,
      }

      const { request } = await publicClient.simulateContract({
        address: queueAddress,
        abi: oneToOneQueueAbi,
        functionName: 'submitOrder',
        args: [
          {
            amountOffer: parsedAmount,
            offerAsset: offerTokenAddress,
            wantAsset: wantTokenAddress,
            intendedDepositor: account,
            receiver: account,
            refundReceiver: account,
            signatureParams,
          },
        ],
        chain: hyperEVM,
        account,
      })

      const submitHash = await walletClient.writeContract(request)
      await waitForTxReceipt(publicClient, submitHash)

      void queryClient.invalidateQueries({
        queryKey: getBalancesQueryKeyPrefix({ chainId, account }),
      })
      void queryClient.invalidateQueries({ queryKey: ['readContract'] })
      void queryClient.invalidateQueries({ queryKey: ['readContracts'] })
      await refetchBalance()

      setStatus({
        state: 'success',
        message: 'Order submitted. Your NFT receipt will receive funds when processed.',
        txHash: submitHash,
      })
    } catch (error) {
      setStatus({
        state: 'error',
        message: error instanceof Error ? error.message : 'Unable to submit order.',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        aria-describedby={undefined}
        className="max-w-[820px] gap-0 rounded-xl border border-white/[0.08] bg-[#08090a] p-6 shadow-2xl sm:p-6"
        closeClassName="top-7 right-7 text-muted-foreground"
      >
        <div className="grid gap-6 lg:grid-cols-[minmax(0,440px)_minmax(260px,1fr)]">
          <div className="min-w-0">
            {status.state === 'success' ? (
              <SuccessScreen
                amount={amount}
                action={action}
                assetSymbol={assetSymbol}
                receiveAmount={receiveAmount}
                status={status}
                transactionUrl={transactionUrl ?? ''}
                wantTokenImage={wantTokenImage}
                wantTokenSymbol={wantTokenSymbol}
              />
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-4 pr-10">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full">
                    <TokenImage
                      src={wantTokenImage}
                      alt={wantTokenSymbol}
                      width={48}
                      height={48}
                      className="h-12 w-12 object-cover"
                      tokenAddress={action.wantTokenAddress}
                    />
                  </div>
                  <div className="min-w-0">
                    <DialogTitle className="pr-0 font-semibold text-2xl text-foreground">Mint USDG0</DialogTitle>
                    <div className="truncate text-muted-foreground text-sm">Powered by Paxos</div>
                  </div>
                </div>

                <div className="flex min-h-[88px] flex-col justify-center gap-3 rounded-xl border border-white/[0.08] bg-white/[0.025] px-4 py-3">
                  <div className="flex items-center justify-between">
                    <label className="text-muted-foreground text-sm" htmlFor="market-get-amount">
                      You pay
                    </label>
                    <div className="flex items-center gap-2">
                      {balanceLabel && <span className="text-muted-foreground text-xs">Balance: {balanceLabel}</span>}
                      <button
                        type="button"
                        onClick={handleMax}
                        disabled={balance === undefined || isBusy}
                        className="rounded-md bg-primary/10 px-2 py-0.5 font-medium text-[10px] text-primary hover:bg-primary/20 disabled:opacity-50"
                      >
                        MAX
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <input
                      id="market-get-amount"
                      inputMode="decimal"
                      value={amount}
                      onChange={(event) => {
                        setStatus({ state: 'idle' })
                        setAmount(event.target.value)
                      }}
                      placeholder="0.00"
                      className="min-w-0 flex-1 bg-transparent font-semibold text-2xl text-foreground outline-none placeholder:text-muted-foreground/60"
                      disabled={isBusy}
                    />
                    <div className="flex h-10 shrink-0 items-center gap-2 rounded-full bg-white/[0.08] px-4 font-semibold text-foreground">
                      <TokenImage
                        src={offerTokenImage}
                        alt={action.offerTokenSymbol}
                        width={20}
                        height={20}
                        className="h-5 w-5 rounded-full object-cover"
                        tokenAddress={action.offerTokenAddress}
                      />
                      {action.offerTokenSymbol}
                    </div>
                  </div>
                  {parsedAmount === undefined && <span className="text-red-300 text-xs">Enter a valid amount.</span>}
                  {exceedsBalance ? (
                    <span className="text-red-300 text-xs">Amount exceeds your wallet balance.</span>
                  ) : isBelowMinimum && minimumOrderSizeLabel ? (
                    <span className="text-red-300 text-xs">
                      Minimum order is {minimumOrderSizeLabel} {action.offerTokenSymbol}.
                    </span>
                  ) : null}
                </div>

                <div className="flex min-h-[88px] flex-col justify-center gap-3 rounded-xl border border-white/[0.08] bg-white/[0.025] px-4 py-3">
                  <div className="text-muted-foreground text-sm">You receive (est.)</div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-semibold text-2xl text-foreground">{receiveAmount ?? <Dash />}</span>
                    <div className="flex h-10 shrink-0 items-center gap-2 rounded-full bg-white/[0.08] px-4 font-semibold text-foreground">
                      <TokenImage
                        src={wantTokenImage}
                        alt={wantTokenSymbol}
                        width={20}
                        height={20}
                        className="h-5 w-5 rounded-full object-cover"
                        tokenAddress={action.wantTokenAddress}
                      />
                      {wantTokenSymbol}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3 rounded-xl border border-white/[0.08] bg-white/[0.025] px-5 py-4 text-sm">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">Paxos Fee</span>
                    <span className="font-medium text-foreground">
                      {feePercent} ({feeAmount} {action.offerTokenSymbol})
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">Minimum</span>
                    <span className="font-medium text-foreground">
                      {minimumOrderSizeLabel ? `${minimumOrderSizeLabel} ${action.offerTokenSymbol}` : '-'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">Max processing time</span>
                    <span className="font-medium text-foreground">5 minutes - 24 hours</span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-muted-foreground">Queue</span>
                    <span className="font-medium font-mono text-foreground">
                      <a
                        href={`${hyperEVM.blockExplorers.default.url}/address/${action.queueAddress}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1 transition-colors hover:text-primary"
                      >
                        {shortenQueueAddress(action.queueAddress)}
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </span>
                  </div>
                </div>

                <div className="flex gap-3 rounded-xl border border-[#e5d84f]/25 bg-[#e5d84f]/10 px-4 py-3 text-[#f2e65d] text-xs leading-relaxed">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    You&apos;ll receive an NFT receipt for your order when you submit. USDG0 is delivered automatically
                    once the queue processes your order. Delivery can take 5-24 hours depending on the size of your
                    order.
                  </span>
                </div>

                {status.message && (
                  <div
                    className={
                      status.state === 'error'
                        ? 'rounded-xl bg-red-500/10 p-3 text-red-300 text-sm'
                        : 'rounded-xl bg-primary/10 p-3 text-primary text-sm'
                    }
                  >
                    {status.message}
                  </div>
                )}

                <Button
                  className="w-full rounded-full text-base"
                  onClick={handleSubmit}
                  disabled={account ? !canSubmit : isBusy}
                >
                  {account
                    ? amountIsValid
                      ? isLoadingPermit
                        ? 'Preparing...'
                        : getButtonLabel(status, canUsePermit, permitUnavailable, action.wantTokenSymbol || assetSymbol)
                      : 'Enter an amount'
                    : 'Connect Wallet'}
                </Button>

                <a
                  href="https://usdg0.paxoslabs.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-1.5 text-muted-foreground text-sm transition-colors hover:text-foreground"
                >
                  Learn more
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
          <PendingOrdersPanel account={account} queueAddress={queueAddress} action={action} assetSymbol={assetSymbol} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const marketGetActionDialogConfig: DialogConfig<MarketGetActionDialogProps> = {
  options: {
    closeOnChainChange: true,
  },
  element: MarketGetActionDialog,
}

function SuccessScreen({
  amount,
  action,
  assetSymbol,
  receiveAmount,
  status,
  transactionUrl,
  wantTokenImage,
  wantTokenSymbol,
}: {
  amount: string
  action: MarketGetActionConfig
  assetSymbol: string
  receiveAmount: string | undefined
  status: MintStatus
  transactionUrl: string
  wantTokenImage: string
  wantTokenSymbol: string
}) {
  return (
    <div className="flex flex-col items-center gap-6 py-4 text-center">
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full">
        <TokenImage
          src={wantTokenImage}
          alt={wantTokenSymbol}
          width={64}
          height={64}
          className="h-16 w-16 object-cover"
          tokenAddress={action.wantTokenAddress}
        />
      </div>

      <div className="flex flex-col gap-2">
        <DialogTitle className="pr-0 font-semibold text-2xl text-foreground">Order submitted</DialogTitle>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Your NFT receipt was minted. USDG0 is delivered automatically once the queue processes your order.
        </p>
      </div>

      <div className="flex w-full flex-col gap-3 rounded-xl border border-white/[0.08] bg-white/[0.025] px-5 py-4 text-sm">
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">You paid</span>
          <span className="font-medium text-foreground">
            {amount || '0'} {action.offerTokenSymbol}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground">Estimated receive</span>
          <span className="font-medium text-foreground">
            {receiveAmount ?? <Dash />} {receiveAmount !== undefined && (action.wantTokenSymbol || assetSymbol)}
          </span>
        </div>
        {status.txHash && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">Transaction</span>
            <span className="font-medium font-mono text-foreground">{shortenHash(status.txHash)}</span>
          </div>
        )}
      </div>

      <div className="flex w-full flex-col gap-3">
        {transactionUrl && (
          <LinkButton variant="outline" className="w-full rounded-full text-base" external href={transactionUrl}>
            View transaction
            <ExternalLink className="h-4 w-4" />
          </LinkButton>
        )}
        <LinkButton variant="outline" className="w-full rounded-full text-base" external href={USDG0_BRIDGE_URL}>
          Open Paxos bridge
          <ExternalLink className="h-4 w-4" />
        </LinkButton>
      </div>
    </div>
  )
}
